import React from 'react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Trash2,
  Plus,
  ChevronUp,
  ChevronDown,
  Type,
  FileText,
  Hash,
  Calendar,
  ToggleLeft,
  CircleDot,
  CheckSquare,
  Upload,
  Star,
  HelpCircle,
  AlertCircle,
} from 'lucide-react';
import { LocalQuestion, SECTION_COLORS, getQuestionTypeLabel } from './templateBuilderUtils';
import { QualificationQuestion } from '../lib/api';
import { QuestionAnswerPreview } from '../components/questionnaire/QuestionAnswerPreview';

const QUESTION_TYPE_ICONS: Record<string, React.ReactNode> = {
  text: <Type className="h-3.5 w-3.5" />,
  textarea: <FileText className="h-3.5 w-3.5" />,
  number: <Hash className="h-3.5 w-3.5" />,
  date: <Calendar className="h-3.5 w-3.5" />,
  yes_no: <ToggleLeft className="h-3.5 w-3.5" />,
  single_choice: <CircleDot className="h-3.5 w-3.5" />,
  multiple_choice: <CheckSquare className="h-3.5 w-3.5" />,
  file_upload: <Upload className="h-3.5 w-3.5" />,
  rating: <Star className="h-3.5 w-3.5" />,
};

const QUESTION_TYPES_LIST: Array<{ value: QualificationQuestion['question_type']; label: string; icon: React.ReactNode }> = [
  { value: 'text', label: 'Short Text', icon: <Type className="h-3.5 w-3.5" /> },
  { value: 'textarea', label: 'Long Text', icon: <FileText className="h-3.5 w-3.5" /> },
  { value: 'number', label: 'Number', icon: <Hash className="h-3.5 w-3.5" /> },
  { value: 'date', label: 'Date', icon: <Calendar className="h-3.5 w-3.5" /> },
  { value: 'yes_no', label: 'Yes / No', icon: <ToggleLeft className="h-3.5 w-3.5" /> },
  { value: 'single_choice', label: 'Single Choice', icon: <CircleDot className="h-3.5 w-3.5" /> },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: <CheckSquare className="h-3.5 w-3.5" /> },
  { value: 'file_upload', label: 'File Upload', icon: <Upload className="h-3.5 w-3.5" /> },
  { value: 'rating', label: 'Rating', icon: <Star className="h-3.5 w-3.5" /> },
];

function getQuestionTypeIcon(type: string): React.ReactNode {
  return QUESTION_TYPE_ICONS[type] ?? <Type className="h-3.5 w-3.5" />;
}

export interface QuestionEditorProps {
  question: LocalQuestion;
  qIdx: number;
  totalQuestions: number;
  sectionKey: string;
  sectionColor: typeof SECTION_COLORS[number];
  onUpdate: (sectionKey: string, questionKey: string, field: keyof LocalQuestion, value: unknown) => void;
  onRemove: (sectionKey: string, questionKey: string) => void;
  onMoveUp: (sectionKey: string, qIdx: number) => void;
  onMoveDown: (sectionKey: string, qIdx: number) => void;
  onAddChoice: (sectionKey: string, questionKey: string) => void;
  onUpdateChoice: (sectionKey: string, questionKey: string, idx: number, value: string) => void;
  onRemoveChoice: (sectionKey: string, questionKey: string, idx: number) => void;
  /** CR-5918: inline validation message shown on the answer-choices block when set. */
  errorMessage?: string;
}

export function QuestionEditor({
  question,
  qIdx,
  totalQuestions,
  sectionKey,
  sectionColor,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onAddChoice,
  onUpdateChoice,
  onRemoveChoice,
  errorMessage,
}: QuestionEditorProps) {
  const hasChoices = question.question_type === 'single_choice' || question.question_type === 'multiple_choice';

  return (
    <div className="group relative rounded-lg border bg-white hover:shadow-md transition-shadow">
      {/* Left accent */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${sectionColor.border.replace('border-', 'bg-')}`} />

      <div className="pl-5 pr-4 py-4 space-y-4">
        {/* Question header row */}
        <div className="flex items-start gap-3">
          {/* Question number badge */}
          <div className={`flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full ${sectionColor.badge} text-xs font-bold mt-0.5`}>
            Q{qIdx + 1}
          </div>

          <div className="flex-1 min-w-0 space-y-3">
            {/* Question text + type row */}
            <div className="flex items-center gap-2">
              <Input
                value={question.question_text}
                onChange={(e) => onUpdate(sectionKey, question._key, 'question_text', e.target.value)}
                placeholder="Enter your question..."
                className="flex-1 font-medium"
              />
              <Select
                value={question.question_type}
                onValueChange={(v) => onUpdate(sectionKey, question._key, 'question_type', v)}
              >
                <SelectTrigger className="w-[170px]">
                  <div className="flex items-center gap-2">
                    {getQuestionTypeIcon(question.question_type)}
                    <span>{getQuestionTypeLabel(question.question_type)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES_LIST.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        {t.icon}
                        <span>{t.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Help text */}
            <div className="flex items-start gap-2">
              <HelpCircle className="h-4 w-4 text-muted-foreground mt-2.5 flex-shrink-0" />
              <Input
                value={question.help_text}
                onChange={(e) => onUpdate(sectionKey, question._key, 'help_text', e.target.value)}
                placeholder="Help text or guidance for respondents (optional)"
                className="text-sm"
              />
            </div>

            {/* Choice options */}
            {hasChoices && (
              <div
                className={`ml-1 space-y-2 rounded-lg border p-3 ${
                  errorMessage ? 'border-red-300 bg-red-50' : 'bg-gray-50'
                }`}
              >
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Answer Choices</Label>
                {question.choices.map((choice, cIdx) => (
                  <div key={cIdx} className="flex items-center gap-2">
                    <div className="flex items-center justify-center h-6 w-6 rounded-full border-2 border-gray-300 text-xs text-muted-foreground font-medium flex-shrink-0">
                      {question.question_type === 'single_choice' ? (
                        <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                      ) : (
                        <span>{String.fromCharCode(65 + cIdx)}</span>
                      )}
                    </div>
                    <Input
                      value={choice}
                      onChange={(e) => onUpdateChoice(sectionKey, question._key, cIdx, e.target.value)}
                      placeholder={`Option ${cIdx + 1}`}
                      className="text-sm flex-1 bg-white"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-red-500"
                      onClick={() => onRemoveChoice(sectionKey, question._key, cIdx)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddChoice(sectionKey, question._key)}
                  className="text-xs text-primary hover:text-primary"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Choice
                </Button>
                {errorMessage && (
                  <p className="flex items-center gap-1.5 text-xs font-medium text-red-600" role="alert">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    {errorMessage}
                  </p>
                )}
              </div>
            )}

            {/* Answer preview — non-choice types have no inline editor, so the
                builder otherwise sees nothing and assumes the type is broken.
                Show a read-only copy of the supplier's answer control so it is
                obviously working without opening the full Preview dialog.
                Choice types are skipped: their Answer Choices editor above
                already shows what suppliers will see. */}
            {!hasChoices && (
              <div className="ml-1 space-y-2 rounded-lg border border-dashed bg-gray-50 p-3">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Answer Preview
                </Label>
                <div className="pointer-events-none">
                  <QuestionAnswerPreview
                    questionType={question.question_type}
                    choices={question.choices}
                    idKey={question._key}
                  />
                </div>
              </div>
            )}

            {/* Bottom controls */}
            <div className="flex items-center gap-3 pt-1 border-t">
              <div className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50">
                <Switch
                  id={`req-${question._key}`}
                  checked={question.is_required}
                  onCheckedChange={(v) => onUpdate(sectionKey, question._key, 'is_required', v)}
                />
                <Label htmlFor={`req-${question._key}`} className="text-xs cursor-pointer flex items-center gap-1">
                  {question.is_required && <AlertCircle className="h-3 w-3 text-amber-500" />}
                  Required
                </Label>
              </div>
              <div className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-50">
                <Switch
                  id={`scored-${question._key}`}
                  checked={question.is_scored}
                  onCheckedChange={(v) => onUpdate(sectionKey, question._key, 'is_scored', v)}
                />
                <Label htmlFor={`scored-${question._key}`} className="text-xs cursor-pointer">Scored</Label>
              </div>
              {question.is_scored && (
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-muted-foreground">Max:</Label>
                  <Input
                    type="number"
                    min={0}
                    value={question.max_score}
                    onChange={(e) =>
                      onUpdate(sectionKey, question._key, 'max_score', parseInt(e.target.value, 10) || 0)
                    }
                    className="h-7 w-20 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">pts</span>
                </div>
              )}
              <div className="flex-1" />
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onMoveUp(sectionKey, qIdx)}
                      disabled={qIdx === 0}
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Move up</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => onMoveDown(sectionKey, qIdx)}
                      disabled={qIdx === totalQuestions - 1}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Move down</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => onRemove(sectionKey, question._key)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete question</TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
